import * as Sentry from '@sentry/react'
import { UAParser } from 'ua-parser-js'
import { LocalInsert } from '@weblite/persist'
import { SessionStorageKeys } from '$logic/client-device/types'
import {
  sessionStorageGetItem,
  sessionStorageSetItem,
} from '$func/session-storage'
import isUaWebView from 'is-ua-webview'

const isInTwa = () => {
  try {
    const wasTwa = sessionStorageGetItem(SessionStorageKeys.IS_TWA)
    if (wasTwa != null) return wasTwa
    // eslint-disable-next-line no-empty
  } catch (e) {}

  const params = new URLSearchParams(window.location.search)

  const twaPort = params.get('twa_port')
  if (twaPort) sessionStorageSetItem(SessionStorageKeys.TWA_PORT, twaPort)

  const twaWsPort = params.get('twa_ws_port')
  const twaWsToken = params.get('twa_ws_token') || ''
  if (twaWsPort) {
    sessionStorageSetItem(
      SessionStorageKeys.TWA_WS_URL,
      `ws://127.0.0.1:${twaWsPort}/${twaWsToken}`,
    )
  }

  const isTWA =
    document.referrer.includes('android-app://') ||
    params.get('utm_source') === 'twa' ||
    params.get('twa_version') != null
  sessionStorageSetItem(SessionStorageKeys.IS_TWA, String(isTWA))

  if (isTWA) LocalInsert('twaVersion', parseInt(params.get('twa_version')!, 10))
  return isTWA
}

const isNumber = (x: string) => /^\d+$/.test(x)

/**
 * => null versions can not be compared
 * => 1 if first > second
 * => -1 if second > first
 * => 0 if first === second
 */
const compare = (first: string, second: string) => {
  const v1 = first.split('.').filter(isNumber).map(Number)
  const v2 = second.split('.').filter(isNumber).map(Number)

  if (v1.length === 0 || v2.length === 0) return null

  // Zero padding
  while (v1.length < v2.length) v1.push(0)
  while (v2.length < v1.length) v2.push(0)

  // compare
  for (let i = 0; i < v1.length; ++i) {
    if (v1[i] === v2[i]) continue
    else if (v1[i] > v2[i]) return 1
    else return -1
  }

  return 0
}

interface ErrorPageUrl {
  url: string
  search?: string
}

// not supported version
export const lt = (first: string, second: string) =>
  compare(first, second) === -1

enum OsType {
  iOS = 'iOS',
  Windows = 'Windows',
  Mac = 'Mac OS',
  Android = 'Android',
  Linux = 'linux',
  Other = 'other',
}

export enum BrowserName {
  Chrome = 'Chrome',
  Chromium = 'Chromium',
  Firefox = 'Firefox',
  Mozilla = 'Mozilla',
  Edge = 'Edge',
  Safari = 'Safari',
  MobileSafari = 'Mobile Safari',
  SamsungBrowser = 'Samsung Browser',
}

//  @ts-expect-error there is a chance that opera browser don't have window.opera
const UA = navigator.userAgent || navigator.vendor || window.opera
const parser = new UAParser(UA)

const { name: OS_NAME, version: OS_VERSION } = parser.getOS()
const { name: BROWSER, version: BROWSER_VERSION } = parser.getBrowser()

const isLinux = new Set(['Ubuntu', 'Mint', 'Linux', 'Debian', 'Fedora']).has(
  OS_NAME || '',
)

const isAndroid = OS_NAME === OsType.Android

const OS_TYPE = (() => {
  if (!OS_NAME) return 'other' as OsType

  if (new Set(['iOS', 'Mac OS', 'Windows', 'Android']).has(OS_NAME))
    return OS_NAME as OsType

  if (isLinux) return 'linux' as OsType

  return 'other' as OsType
})()

export const minimumOsRequirements: Record<OsType, string> = {
  iOS: '0.0',
  'Mac OS': '0.0',
  Windows: '0.0',
  Android: '7.0.0',
  linux: '0.0',
  other: '0.0',
}

export const minimumBrowserRequirements: Record<
  OsType,
  Partial<Record<BrowserName, string>>
> = {
  iOS: {
    Safari: '15.6',
    'Mobile Safari': '15.6',
    Chrome: '109',
    Firefox: '115',
    Edge: '109',
  },
  'Mac OS': {
    Safari: '16.6',
    Chrome: '109',
    Firefox: '115',
    Edge: '109',
  },
  Windows: {
    Chrome: '109',
    Chromium: '109',
    Firefox: '115',
    Mozilla: '115',
    Edge: '109',
  },
  Android: {
    'Samsung Browser': '20',
    Chrome: '109',
    Firefox: '115',
    Mozilla: '115',
    Edge: '109',
  },
  linux: {
    Chrome: '109',
    Chromium: '109',
    Firefox: '115',
    Mozilla: '115',
    Edge: '109',
  },
  other: {
    Chrome: '109',
    Edge: '109',
    Firefox: '115',
    'Mobile Safari': '15.6',
    Safari: '16.6',
  },
}

const isIOS =
  OS_NAME === OsType.iOS ||
  (window.orientation !== undefined && OS_NAME === 'Mac OS') // for iPadOS

const isMacOS = OS_NAME === OsType.Mac
export const isSupportedBrowser = () => {
  if (!BROWSER) return true
  const osTypeRequirements = minimumBrowserRequirements[OS_TYPE]

  if (!osTypeRequirements) return true

  return BROWSER in osTypeRequirements
}

export const getOutDatedBrowserMeta = () => {
  const browser = BROWSER as BrowserName

  const version = minimumBrowserRequirements[OS_TYPE]?.[browser]

  if (browser && version && BROWSER_VERSION)
    return {
      clientBrowser: browser,
      clientBrowserVersion: BROWSER_VERSION,
      recommendedBrowserVersion: version,
    }
  return null
}

export const isOutDatedBrowser = () => {
  if (!BROWSER || !BROWSER_VERSION) return false

  const browser = BROWSER as BrowserName
  const minBrowserVersion = minimumBrowserRequirements[OS_TYPE]?.[browser]

  if (minBrowserVersion) return lt(BROWSER_VERSION, minBrowserVersion)

  return false
}

export const isOutDatedOs = () => {
  const minOsVersion = minimumOsRequirements[OS_NAME as OsType]
  if (!OS_NAME || OS_NAME !== OsType.Android || !OS_VERSION || !minOsVersion)
    return false

  return lt(OS_VERSION, minOsVersion)
}

export const isWebView = isUaWebView(UA)

// const isCriticalError = (error: Error) => {
//   return (
//     error instanceof ReferenceError ||
//     error instanceof TypeError ||
//     error instanceof DOMException
//   )
// }

const isCookieEnabled = () => {
  if (!('cookie' in document)) return false
  if (document.cookie.length > 0) return true
  document.cookie = 'cookietest=1'
  const ret = document.cookie.indexOf('cookietest=') !== -1
  document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT'
  return ret
}

const isStorageEnabled = () => {
  try {
    const testKey = 'test'
    localStorage.setItem(testKey, 'testValue')
    localStorage.removeItem(testKey)
    return true
  } catch (e) {
    return false
  }
}

const ERROR_PAGES_DIR_PATH = 'error-pages'

export enum WARNING_PAGES {
  cookieDisabled = 'cookieDisabled',
  storageDisabled = 'storageDisabled',
  installTWA = 'installTWA',
  // isWebView = 'isWebView',
  browserNotSupported = 'browserNotSupported',
  outdatedBrowser = 'outdatedBrowser',
  outdatedOs = 'outdatedOs',
}

export const ERROR_PAGES_PATHS: Record<WARNING_PAGES, string> = {
  cookieDisabled: 'cookie-disabled.html',
  storageDisabled: 'storage-disabled.html',
  installTWA: 'install-twa.html',
  // isWebView: 'web-view.html',
  browserNotSupported: 'browser-not-supported.html',
  outdatedBrowser: 'out-dated-browser.html',
  outdatedOs: 'out-dated-os.html',
}

export function getBrowserCompatibilityWarningPage(
  sendAnalytics = false,
): WARNING_PAGES | undefined {
  let result: WARNING_PAGES | undefined = undefined
  if (!isSupportedBrowser()) result = WARNING_PAGES.browserNotSupported
  else if (isOutDatedBrowser()) {
    const outDatedBrowserMeta = getOutDatedBrowserMeta()

    if (outDatedBrowserMeta) result = WARNING_PAGES.outdatedBrowser
  } else if (isOutDatedOs()) result = WARNING_PAGES.outdatedOs

  if (result && sendAnalytics)
    analite?.('SHOW_ERROR_ASSISTANT_MODAL', {
      errorPage: result,
      isIOS,
      isAndroid,
      isInTwa,
      isWebView,
      browser: BROWSER,
      osType: OS_TYPE,
      isLinux,
      browserVersion: BROWSER_VERSION,
      osVersion: OS_VERSION,
      ua: UA,
      isMacOS,
    })
  return result
}

function getBrowserCompatibilityErrorPageUrl(): ErrorPageUrl | undefined {
  const warningPage = getBrowserCompatibilityWarningPage()
  if (warningPage === WARNING_PAGES.browserNotSupported) {
    return {
      url: [ERROR_PAGES_DIR_PATH, ERROR_PAGES_PATHS.browserNotSupported].join(
        '/',
      ),
      search: new URLSearchParams({
        isIOS: String(isIOS || isMacOS),
        isTWA: String(Boolean(isInTwa())),
      }).toString(),
    }
  }
  if (warningPage === WARNING_PAGES.outdatedBrowser) {
    const outDatedBrowserMeta = getOutDatedBrowserMeta()

    if (outDatedBrowserMeta) {
      const { clientBrowser, clientBrowserVersion, recommendedBrowserVersion } =
        outDatedBrowserMeta
      return {
        url: [ERROR_PAGES_DIR_PATH, ERROR_PAGES_PATHS.outdatedBrowser].join(
          '/',
        ),
        search: new URLSearchParams({
          clientBrowser,
          clientBrowserVersion,
          recommendedBrowserVersion,
        }).toString(),
      }
    }
  }

  if (warningPage === WARNING_PAGES.outdatedOs) {
    return {
      url: [ERROR_PAGES_DIR_PATH, ERROR_PAGES_PATHS.outdatedOs].join('/'),
      search: new URLSearchParams({
        isAndroid: String(isAndroid),
        isIOS: String(isIOS || isMacOS),
      }).toString(),
    }
  }
  return undefined
}

export function getErrorPageUrl(): ErrorPageUrl | undefined {
  if (!isCookieEnabled()) {
    return {
      url: [ERROR_PAGES_DIR_PATH, ERROR_PAGES_PATHS.cookieDisabled].join('/'),
    }
  }
  if (!isStorageEnabled()) {
    return {
      url: [ERROR_PAGES_DIR_PATH, ERROR_PAGES_PATHS.storageDisabled].join('/'),
    }
  }
  // if (isWebView && (isAndroid || isIOS)) {
  //   return {
  //     url: [ERROR_PAGES_DIR_PATH, ERROR_PAGES_PATHS.isWebView].join('/'),
  //     search: new URLSearchParams({ isAndroid: String(isAndroid) }).toString(),
  //   }
  // }
  return getBrowserCompatibilityErrorPageUrl()
}

export function calErrorPageUrl(errorPageUrl: ErrorPageUrl) {
  return `${errorPageUrl.url}?${errorPageUrl.search || ''}`
}

function checkError() {
  const errorPageUrl = getErrorPageUrl()
  if (errorPageUrl) {
    Sentry.setTag('errorPageUrl', calErrorPageUrl(errorPageUrl))
    setTimeout(() => {
      window.location.href = new URL(
        calErrorPageUrl(errorPageUrl),
        window.location.origin,
      ).toString()
    }, 0)
  }
}

function getBrowserCompatibilityUrl() {
  const errorPageUrl = getBrowserCompatibilityErrorPageUrl()
  if (errorPageUrl)
    return new URL(
      calErrorPageUrl(errorPageUrl),
      window.location.origin,
    ).toString()
}

function checkBrowserCompatibility() {
  const browserCompatibilityUrl = getBrowserCompatibilityUrl()
  if (browserCompatibilityUrl) window.location.href = browserCompatibilityUrl
}

if (
  window.config.frontend.VITE_CHECK_BROWSER_COMPATIBILITY_ON_LOAD === 'true'
) {
  checkBrowserCompatibility()
}

window.addEventListener(
  'error',
  () => {
    checkError()
    return true
  },
  false,
)
